import React, { Suspense, useRef, useEffect, useState, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";
import { MASTER_LOGO, DIVISIONS } from "@/data/divisions";

/* ---------- Signature 3D scene: 7 brushstroke ink-ribbons ----------
 * One per division, painted in that division's brand color.
 * Each ribbon is a TubeGeometry along a hand-authored 3D curve —
 * evoking a wet paint brushstroke and directly referencing the
 * hand-brushed lettering + kiss motif on every 2T logo.
 * The whole cluster slowly rotates like a floating sculpture and
 * responds to cursor movement (parallax tilt).
 */

// Distinct signature palette (overrides shared accents so all 7 read differently)
const RIBBON_COLORS = {
  print: "#22d3ee", // cyan
  shirt: "#f472b6", // pink
  sticker: "#a78bfa", // violet
  deco: "#f5c542", // gold
  pack: "#4ade80", // green
  wrap: "#8b5cf6", // purple
  prod: "#ef4444", // red
};

// Deterministic curve generator — each division gets a unique brushstroke path.
const makeBrushCurve = (seed) => {
  const points = [];
  const s = seed * 0.618; // golden-ratio phase per division
  const segments = 9;
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const angle = t * Math.PI * 2 + s;
    const radius = 1.6 + Math.sin(t * Math.PI * 3 + s) * 0.9;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle * 0.7 + s) * 1.8;
    const z = Math.sin(angle * 1.3 + s * 2) * 1.3;
    points.push(new THREE.Vector3(x, y, z));
  }
  return new THREE.CatmullRomCurve3(points, true, "catmullrom", 0.55);
};

const Ribbon = ({ color, seed, speed = 0.15, mouse }) => {
  const groupRef = useRef();
  const kissRef = useRef();
  const curve = useMemo(() => makeBrushCurve(seed), [seed]);
  const geometry = useMemo(
    () => new THREE.TubeGeometry(curve, 220, 0.055, 10, true),
    [curve]
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const mx = mouse.current.x;
    const my = mouse.current.y;
    if (groupRef.current) {
      groupRef.current.rotation.x = Math.sin(t * speed + seed) * 0.35 + my * 0.25;
      groupRef.current.rotation.y = t * speed + seed * 1.4 + mx * 0.35;
      groupRef.current.rotation.z = Math.cos(t * speed * 0.7 + seed) * 0.2;
    }
    if (kissRef.current) {
      // Kiss mark orbits its ribbon (signature dot at the tip of the brushstroke)
      const kt = (t * 0.09 + seed * 0.13) % 1;
      const p = curve.getPoint(kt);
      kissRef.current.position.set(p.x, p.y, p.z);
      kissRef.current.scale.setScalar(0.16 + Math.sin(t * 2 + seed) * 0.03);
    }
  });

  return (
    <group ref={groupRef}>
      <mesh geometry={geometry}>
        <meshBasicMaterial color={color} transparent opacity={0.62} />
      </mesh>
      {/* Kiss mark bead — the signature dot */}
      <mesh ref={kissRef}>
        <sphereGeometry args={[1, 20, 20]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} />
      </mesh>
    </group>
  );
};

const InkDust = () => {
  // Sparse floating particles = ink dust
  const ref = useRef();
  const [positions, colors] = useMemo(() => {
    const count = 90;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const palette = Object.values(RIBBON_COLORS).map((h) => new THREE.Color(h));
    for (let i = 0; i < count; i += 1) {
      pos[i * 3 + 0] = (Math.random() - 0.5) * 8;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 6;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 6;
      const c = palette[i % palette.length];
      col[i * 3 + 0] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    return [pos, col];
  }, []);
  useFrame((state) => {
    if (ref.current) ref.current.rotation.y = state.clock.elapsedTime * 0.03;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        transparent
        opacity={0.7}
        vertexColors
        sizeAttenuation
      />
    </points>
  );
};

const HeroScene = ({ mouse }) => (
  <Canvas
    camera={{ position: [0, 0, 7], fov: 45 }}
    dpr={[1, 1.75]}
    gl={{ antialias: true, alpha: true }}
    style={{ background: "transparent" }}
  >
    <Suspense fallback={null}>
      {DIVISIONS.map((d, i) => (
        <Ribbon
          key={d.slug}
          color={RIBBON_COLORS[d.slug] || d.accent}
          seed={i * 1.1}
          speed={0.12 + (i % 3) * 0.03}
          mouse={mouse}
        />
      ))}
      <InkDust />
    </Suspense>
  </Canvas>
);

/* ---------- Masked line reveal ---------- */
const LineReveal = ({ children, delay = 0 }) => (
  <span className="mask-line block">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: [0.76, 0, 0.24, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ onScrollDown, ready }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const [count, setCount] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  // Mouse parallax
  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Number counter on ready
  useEffect(() => {
    if (!ready) return undefined;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / 1800);
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * 7));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  const baseDelay = ready ? 0 : 0.4;

  return (
    <section
      ref={ref}
      className="relative min-h-screen w-full flex flex-col justify-end overflow-hidden"
      data-testid={HOME.heroTitle}
    >
      {/* ambient color blobs (parallax) */}
      <motion.div
        style={{ scale: bgScale }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="blob"
          style={{
            background: "#ec4899",
            width: 620,
            height: 620,
            top: "-12%",
            left: "-8%",
          }}
        />
        <div
          className="blob"
          style={{
            background: "#22d3ee",
            width: 480,
            height: 480,
            bottom: "-14%",
            right: "-6%",
            opacity: 0.35,
          }}
        />
        <div
          className="blob"
          style={{
            background: "#f5c542",
            width: 320,
            height: 320,
            top: "20%",
            right: "18%",
            opacity: 0.28,
          }}
        />
      </motion.div>

      {/* 3D canvas */}
      <div className="absolute inset-0 pointer-events-none opacity-75">
        <HeroScene mouse={mouse} />
      </div>

      {/* Master logo with dark halo removed — nav logo grows/shrinks on scroll instead */}

      {/* Kicker removed — giant nav logo is the hero branding */}

      {/* Right counter */}
      <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 flex items-end gap-3 text-white/60">
        <div className="text-right">
          <div className="text-[0.58rem] tracking-hair uppercase text-white/40 mb-1">
            Divisions
          </div>
          <div className="font-display text-4xl md:text-6xl leading-none text-white">
            0{count}
            <span className="text-white/30">/07</span>
          </div>
        </div>
      </div>

      {/* Kinetic headline — sized so 4 lines always fit under the giant nav logo */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 px-6 md:px-10 pt-56 md:pt-64 lg:pt-72 pb-24 md:pb-28"
      >
        <h1 className="font-display uppercase text-white text-[13vw] md:text-[9.5vw] lg:text-[7.5vw] xl:text-[6.8vw] leading-[0.86] tracking-tight">
          <LineReveal delay={baseDelay + 0.2}>{t.hero.line1}</LineReveal>
          <LineReveal delay={baseDelay + 0.35}>
            <span className="text-white">{t.hero.line2.split(",")[0]}</span>
            <span className="font-editorial italic text-white/70">
              {t.hero.line2.includes(",") ? "," + t.hero.line2.split(",")[1] : ""}
            </span>
          </LineReveal>
          <LineReveal delay={baseDelay + 0.5}>{t.hero.line3}</LineReveal>
          <LineReveal delay={baseDelay + 0.65}>
            <span
              className="ink-underline"
              style={{ "--accent": "#ec4899" }}
            >
              {t.hero.line4}
            </span>
          </LineReveal>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: baseDelay + 1.5 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <p className="max-w-lg text-white/75 text-base md:text-lg leading-relaxed">
            {t.hero.sub}
          </p>
          <button
            data-testid={HOME.heroCta}
            onClick={onScrollDown}
            className="btn-inky self-start md:self-auto"
          >
            <span>{t.hero.cta}</span>
            <span className="text-lg leading-none">↓</span>
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: baseDelay + 1.8 }}
        data-testid={HOME.scrollIndicator}
        className="absolute bottom-6 left-6 md:left-10 flex items-center gap-3 text-[0.65rem] tracking-hair uppercase text-white/50"
      >
        <motion.span
          animate={{ scaleX: [0, 1, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-block w-12 h-px bg-white/60 origin-left"
        />
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};

export default Hero;
